import { LoadingManager, Box3, Mesh, MeshPhysicalMaterial, MeshStandardMaterial, Object3D, Vector3 } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import type { WebGLRenderer } from 'three';
import type { FrameSpec } from '../config/frames';
import { FrameObject } from './FrameObject';
import { buildProceduralFrame } from './ProceduralFrame';

let sharedLoader: GLTFLoader | null = null;

/**
 * GLTFLoader with Draco (geometry) and KTX2/Basis (textures) decoders, served
 * from our own origin (copied to /public on postinstall).
 */
export function getGltfLoader(renderer: WebGLRenderer, base = ''): GLTFLoader {
  if (sharedLoader) return sharedLoader;
  const loader = new GLTFLoader();
  const draco = new DRACOLoader();
  draco.setDecoderPath(`${base}draco/`);
  draco.setDecoderConfig({ type: 'wasm' });
  loader.setDRACOLoader(draco);
  const ktx2 = new KTX2Loader();
  ktx2.setTranscoderPath(`${base}basis/`);
  ktx2.detectSupport(renderer);
  loader.setKTX2Loader(ktx2);
  sharedLoader = loader;
  return loader;
}

export interface LoadReport {
  source: 'glb' | 'procedural';
  /** Measured hinge-to-hinge width of the loaded model (mm), for the QA check-list. */
  measuredWidthMm: number;
  warnings: string[];
}

/**
 * Loads the frame for `spec`: the GLB when `glbUrl` is set (validating the
 * naming convention and the scale), otherwise the procedural placeholder.
 */
export async function loadFrame(spec: FrameSpec, renderer: WebGLRenderer, assetBase = ''): Promise<{ frame: FrameObject; report: LoadReport }> {
  if (!spec.glbUrl) {
    const manager = new LoadingManager();
    let failed = false;
    const ready = new Promise<void>(resolve => { manager.onLoad = resolve; });
    manager.onError = () => { failed = true; };
    // Sentinel covers models with no photos and waits for every texture before swap.
    manager.itemStart('frame-build');
    const frame = buildProceduralFrame(spec, manager);
    manager.itemEnd('frame-build');
    await ready;
    if (failed) { frame.dispose(); throw new Error('Frame texture could not be loaded'); }
    return { frame, report: { source: 'procedural', measuredWidthMm: measureWidth(frame.model), warnings: [] } };
  }

  const loader = getGltfLoader(renderer, assetBase);
  const gltf = await loader.loadAsync(spec.glbUrl);
  const model: Object3D = gltf.scene;
  const warnings: string[] = [];

  // glTF is metres; our convention is millimetres. Detect and convert.
  const width = measureWidth(model);
  if (width > 0 && width < 1) {
    model.scale.multiplyScalar(1000);
    model.updateMatrixWorld(true);
    warnings.push('GLB appears to be in metres, scaled x1000 to millimetres.');
  }
  const measured = measureWidth(model);
  if (Math.abs(measured - spec.dims.widthMm) > 3) {
    warnings.push(`Measured width ${measured.toFixed(1)} mm differs from catalogue width ${spec.dims.widthMm} mm.`);
  }

  const hingeL = model.getObjectByName('hinge_L') ?? null;
  const hingeR = model.getObjectByName('hinge_R') ?? null;
  if (!hingeL || !hingeR) warnings.push('hinge_L / hinge_R nodes not found: temple spread disabled.');

  model.traverse((o) => {
    if (o instanceof Mesh) {
      o.castShadow = !o.name.startsWith('lens');
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      for (const m of mats) {
        // Lenses exported as standard materials get a physical upgrade for clearcoat.
        if (o.name.startsWith('lens') && m instanceof MeshStandardMaterial && !(m instanceof MeshPhysicalMaterial)) {
          m.transparent = true;
          m.depthWrite = false;
          if (m.opacity >= 1) m.opacity = spec.look.lensOpacity;
        }
      }
    }
  });

  const frame = new FrameObject(spec, model, hingeL, hingeR);
  return { frame, report: { source: 'glb', measuredWidthMm: measured, warnings } };
}

const _box = new Box3();
const _size = new Vector3();

function measureWidth(model: Object3D): number {
  model.updateMatrixWorld(true);
  _box.setFromObject(model);
  _box.getSize(_size);
  return _size.x;
}
